/* eslint-disable jsdoc/require-jsdoc */
import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import { ParametersType } from '../../constants/telosair-interfaces';
import Deque from '../../classes/deque';
import { getTimeseriesInsights } from '../../services/ai.service';
import Loader from '../../components/loader';
import MarkdownText from '../../components/Data/Markdown';
import usePlan from '../../hooks/usePlan';
import PlanGate from '../../components/PlanGate';

interface InsightsContainerProps {
  expanded: boolean;
  lineChartGraphData: Record<ParametersType, Deque>;
  loading: boolean;
}

export default function InsightsContainer({
  expanded,
  lineChartGraphData,
  loading
}: InsightsContainerProps): ReactElement {
  const navigate = useNavigate();
  const { hasAccess } = usePlan();
  const [insightsLoading, setInsightsLoading] = useState(true);
  const insightsGenerated = useRef(false);
  const [insights, setInsights] = useState<string>('');
  const scrollableContainerRef = useRef<HTMLDivElement>(null);
  const scrollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const userHasScrolled = useRef(false);

  const stopAutoScroll = (): void => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
  };

  const handleUserScroll = (): void => {
    if (!userHasScrolled.current) {
      userHasScrolled.current = true;
      stopAutoScroll();
    }
  };

  const fetchInsights = async (): Promise<void> => {
    try {
      const newInsights = await getTimeseriesInsights(lineChartGraphData);
      setInsights(newInsights);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setInsightsLoading(false);
    }
  };

  useEffect(() => {
    if (!insightsGenerated.current && !loading) {
      if (Object.keys(lineChartGraphData).length > 0) {
        fetchInsights();
        insightsGenerated.current = true;
      } else {
        setInsightsLoading(false);
      }
    }
  }, [lineChartGraphData, loading]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (!insightsLoading && insights && !userHasScrolled.current) {
      const startScrollTimeout = setTimeout(() => {
        if (
          scrollableContainerRef.current &&
          scrollableContainerRef.current.scrollHeight > scrollableContainerRef.current.clientHeight
        ) {
          scrollIntervalRef.current = setInterval(() => {
            if (userHasScrolled.current) {
              stopAutoScroll();

              return;
            }
            if (scrollableContainerRef.current) {
              const { scrollTop, scrollHeight, clientHeight } = scrollableContainerRef.current;
              if (scrollTop + clientHeight >= scrollHeight) {
                stopAutoScroll();
              } else {
                scrollableContainerRef.current.scrollTop += 1;
              }
            }
          }, 50);
        }
      }, 5000);

      return () => {
        clearTimeout(startScrollTimeout);
        stopAutoScroll();
      };
    }
  }, [insightsLoading, insights]);

  useEffect(
    () => () => {
      stopAutoScroll();
    },
    []
  );

  return (
    <div
      className={`flex flex-col bg-background-700 col-span-1 p-4 h-[12rem] ${
        expanded ? 'md:col-span-3' : 'md:col-span-2'
      }`}
    >
      <div className="flex w-full">
        <Header
          proSubscription
          sectionName="Insights"
          showIcon
          actionButton={() => navigate('/settings/device-setup?tab=groups')}
          actionButtonText="Settings"
        >
          <div className="flex items-center gap-2">
            <span className="font-regular text-gray-400 cursor-default">All Devices</span>
          </div>
        </Header>
      </div>
      <div className={`flex flex-1 flex-col w-full mt-2 ${hasAccess('Advanced') ? 'overflow-y-auto' : 'overflow-hidden'}`}>
        {!hasAccess('Advanced') && <PlanGate requiredPlan="Advanced"><span /></PlanGate>}
        {hasAccess('Advanced') && insightsLoading && (
          <div className="flex flex-1 items-center justify-center relative">
            <Loader />
          </div>
        )}
        {hasAccess('Advanced') && !insightsLoading && insights && (
          <div
            ref={scrollableContainerRef}
            onWheel={handleUserScroll}
            onTouchStart={handleUserScroll}
            className="flex-1 text-gray-300 text-lg overflow-auto pr-2"
            style={{
              // Removed gradient mask for scrollable content
              maxHeight: '7rem'
            }}
          >
            <MarkdownText>{insights}</MarkdownText>
          </div>
        )}
        {hasAccess('Advanced') && !insightsLoading && !insights && (
          <div className="flex flex-1 justify-center items-center h-full text-gray-400 font-medium">
            No Insights Available
          </div>
        )}
      </div>
    </div>
  );
}
